/**
 * PermisEspaceConfineForm.jsx — Analyse, check-list et permis d'entrer en espace confiné (Lafarge)
 * Reproduit les blocs du template (Permis_dentrer_EC) :
 *   - Sélecteur Espace Confiné (Liste_EC du site)
 *   - Préparation : consignation matière, balisage, affichage, restriction d'accès
 *   - 1. Atmosphère dangereuse (asphyxie/toxicité — process + créée par travaux)
 *   - 1.2 Moyens de prévention (contrôle atmosphère, ventilation, T°, ARI, certificat dégazage)
 *   - 2. Enfouissement / Ensevelissement
 *   - 3. Plan de secours EC
 *   - Signataires (auto-import Resp Lafarge + Resp EE depuis AT, datalist Surveillant EC depuis Accueils sécurité)
 */
import { useEffect, useState } from 'react';
import { ShieldAlert, ChevronDown } from 'lucide-react';
import { api } from '@/lib/api';
import FormationPicker from '@/components/FormationPicker';

function Checkbox({ checked, onChange, label, testid, className = '' }) {
  return (
    <label className={`flex items-start gap-2 cursor-pointer text-xs ${className}`} data-testid={testid}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-[#DA291C] flex-shrink-0" />
      <span className="leading-snug">{label}</span>
    </label>
  );
}

function YesNo({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-1" data-testid={testid}>
      {['oui', 'non'].map((v) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-2 py-0.5 text-[11px] font-bold uppercase border ${
            value === v ? (v === 'oui' ? 'bg-green-600 text-white border-green-600' : 'bg-stone-700 text-white border-stone-700') : 'bg-white border-stone-300 text-stone-600'
          }`}>{v}</button>
      ))}
    </div>
  );
}

function NumInput({ value, onChange, testid, suffix, placeholder, width = 'w-20' }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} placeholder={placeholder}
        className={`${width} px-2 py-1 text-xs border border-stone-300 focus:border-[#09090B] outline-none font-mono`} />
      {suffix && <span className="text-[10px] text-stone-500 uppercase">{suffix}</span>}
    </div>
  );
}

function SectionTitle({ children, accent = 'bg-[#09090B]' }) {
  return (
    <div className={`${accent} text-white text-[11px] font-bold uppercase tracking-[0.2em] px-3 py-2`}>
      {children}
    </div>
  );
}

export default function PermisEspaceConfineForm({
  extra,
  setExtra,
  dateDebut,
  dateFin,
  titre,
  lieu,
  entreprise,
  compact = false,
  correspondantLafarge = '',
  correspondantEE = '',
  siteCode = '',
}) {
  const [ecList, setEcList] = useState([]);
  const [inductions, setInductions] = useState([]);

  // Charger Liste_EC et Accueils sécurité
  useEffect(() => {
    api.get('/refs/ec-list', { params: siteCode ? { site: siteCode } : {} })
      .then(({ data }) => setEcList(data || []))
      .catch(() => setEcList([]));
    api.get('/refs/inductions', { params: siteCode ? { site: siteCode } : {} })
      .then(({ data }) => setInductions((data || []).filter((i) => i.valid)))
      .catch(() => setInductions([]));
  }, [siteCode]);

  // Auto-fill Resp Lafarge / Resp EE depuis l'AT (à la première render si vides)
  useEffect(() => {
    if (correspondantLafarge && !extra.resp_lafarge) {
      setExtra('resp_lafarge', correspondantLafarge);
    }
    if (correspondantEE && !extra.resp_ee) {
      setExtra('resp_ee', correspondantEE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correspondantLafarge, correspondantEE]);

  // ID unique pour le datalist Surveillant EC
  const SURV_LIST_ID = 'surveillant-ec-list';

  return (
    <div className="space-y-4 border-2 border-purple-700 bg-white" data-testid="permis-espace-confine-form">
      <div className="bg-purple-700 text-white px-4 py-3 flex items-center gap-3">
        <ShieldAlert className="w-6 h-6" strokeWidth={2.5} />
        <div>
          <div className="text-2xl font-display font-black tracking-tighter">PERMIS D'ENTRER EN ESPACE CONFINÉ</div>
          <div className="text-[10px] uppercase tracking-wider opacity-90">Analyse, check-list et plan de secours EC</div>
        </div>
      </div>

      <div className="px-4 pb-4 space-y-4">
        {/* Sélecteur Espace Confiné — depuis Liste_EC du site */}
        <div>
          <SectionTitle accent="bg-purple-700">Espace Confiné concerné — Liste répertoriée du site</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 bg-purple-50/30">
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">
              Sélectionner l'EC dans la base du site
              {ecList.length > 0 && <span className="ml-2 font-normal italic normal-case text-stone-500">({ecList.length} EC répertoriés)</span>}
            </div>
            <div className="relative">
              <select
                value={extra.ec_selected || ''}
                onChange={(e) => {
                  const code = e.target.value;
                  setExtra('ec_selected', code);
                  const ec = ecList.find((x) => x.code_libelle === code);
                  if (!ec) return;
                  // Métadonnées EC
                  setExtra('ec_type', ec.type);
                  setExtra('ec_risques_maitrises', ec.risques_maitrises);
                  setExtra('ec_risques_non_maitrises', ec.risques_non_maitrises);
                  setExtra('ec_pourquoi_non_maitrises', ec.pourquoi_non_maitrises);
                  // Pré-cochage automatique depuis Liste_EC (Oui/Non → oui/non)
                  const lc = (v) => (v || '').toString().trim().toLowerCase();
                  const yn = (v) => (lc(v) === 'oui' ? 'oui' : lc(v) === 'non' ? 'non' : '');
                  // 1.1.1 Atmosphère - process
                  setExtra('atm_inertage', yn(ec.inertage));
                  setExtra('atm_poussiereuse', yn(ec.poussiere));
                  setExtra('atm_atex', yn(ec.atex));
                  setExtra('atm_chaleur', yn(ec.chaleur));
                  // 1.2 Moyens de prévention
                  // Si Mesure continue OU Mesure à l'entrée = Oui → contrôle d'atmosphère requis
                  const ctrl = (lc(ec.mesure_continue) === 'oui' || lc(ec.mesure_entree) === 'oui') ? 'oui'
                              : (lc(ec.mesure_continue) === 'non' && lc(ec.mesure_entree) === 'non') ? 'non' : '';
                  setExtra('ctrl_atmosphere', ctrl);
                  setExtra('consignation_inertage', yn(ec.consignation_inertage));
                  setExtra('ventilation', yn(ec.ventilation));
                  setExtra('ctrl_temperature', yn(ec.controle_temperature));
                  // 2. Enfouissement / Ensevelissement
                  setExtra('risque_enfouissement', yn(ec.risque_ensevelissement));
                  // Indication ventilation forcée vs naturelle (texte info)
                  const ventInfo = [];
                  if (lc(ec.ventilation_forcee) === 'oui') ventInfo.push('Ventilation forcée');
                  if (lc(ec.ventilation_naturelle) === 'oui') ventInfo.push('Ventilation naturelle');
                  if (lc(ec.suroxygenation) === 'oui') ventInfo.push('⚠ Risque de SUROXYGÉNATION');
                  setExtra('ec_vent_info', ventInfo.join(' · '));
                }}
                data-testid="ec-selector"
                className="w-full px-2 py-2 text-xs border border-stone-300 focus:border-purple-700 outline-none bg-white pr-8 appearance-none"
              >
                <option value="">— Sélectionner un Espace Confiné —</option>
                {ecList.map((ec) => (
                  <option key={ec.code_libelle} value={ec.code_libelle}>
                    {ec.code_libelle} ({ec.type})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500 pointer-events-none" />
            </div>
            {extra.ec_selected && (
              <div className="mt-2 p-2 bg-white border border-purple-200 text-[11px] space-y-1">
                <div><span className="font-bold uppercase tracking-wider text-stone-500">Type :</span> {extra.ec_type || '—'}</div>
                {extra.ec_pourquoi_non_maitrises && (
                  <div><span className="font-bold uppercase tracking-wider text-[#DA291C]">Risques résiduels :</span> <span className="whitespace-pre-line">{extra.ec_pourquoi_non_maitrises}</span></div>
                )}
                {extra.ec_vent_info && (
                  <div><span className="font-bold uppercase tracking-wider text-stone-500">Ventilation :</span> {extra.ec_vent_info}</div>
                )}
                <div className="mt-1 pt-1 border-t border-purple-100 text-[10px] italic text-purple-700">
                  ✓ Les cases OUI/NON ci-dessous ont été pré-cochées depuis la base Liste_EC. Vérifiez et ajustez si nécessaire.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* En-tête — masqué en mode compact (Espace/Équipement = lieu hérité de l'AT) */}
        {!compact && (
        <div className="grid sm:grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Espace / Équipement concerné</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{lieu || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Désignation intervention</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{titre || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Entreprise extérieure</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{entreprise || '—'}</div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1">Validité</div>
            <div className="border border-stone-300 bg-stone-50 px-2 py-1.5">{dateDebut || '—'} → {dateFin || '—'}</div>
          </div>
        </div>
        )}

        {/* Préparation */}
        <div>
          <SectionTitle>À réaliser au préalable</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-2 bg-stone-50/50">
            <Checkbox checked={extra.prep_consignation_matiere} onChange={(v) => setExtra('prep_consignation_matiere', v)} label="Consignation arrivée matière (liquide, solide, gaz)" testid="ec-prep-cons" />
            <Checkbox checked={extra.prep_balisage} onChange={(v) => setExtra('prep_balisage', v)} label="Balisage" testid="ec-prep-balisage" />
            <Checkbox checked={extra.prep_affichage} onChange={(v) => setExtra('prep_affichage', v)} label="Affichage" testid="ec-prep-affichage" />
            <Checkbox checked={extra.prep_restriction} onChange={(v) => setExtra('prep_restriction', v)} label="Restriction d'accès aux personnes autorisées" testid="ec-prep-restrict" />
          </div>
        </div>

        {/* 1. Atmosphère dangereuse */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">1. Atmosphère dangereuse — Risque d'asphyxie / toxicité</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-3 text-xs bg-red-50/30">
            <div>
              <div className="font-bold text-[11px] uppercase tracking-wider mb-2">1.1.1 Causée par le process</div>
              <div className="space-y-1.5 pl-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Présence de gaz</span>
                  <YesNo value={extra.atm_gaz} onChange={(v) => setExtra('atm_gaz', v)} testid="ec-atm-gaz" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Dispositif d'inertage</span>
                  <YesNo value={extra.atm_inertage} onChange={(v) => setExtra('atm_inertage', v)} testid="ec-atm-inert" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Enceinte poussiéreuse</span>
                  <YesNo value={extra.atm_poussiereuse} onChange={(v) => setExtra('atm_poussiereuse', v)} testid="ec-atm-pouss" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Zone ATEX</span>
                  <YesNo value={extra.atm_atex} onChange={(v) => setExtra('atm_atex', v)} testid="ec-atm-atex" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Chaleur de contact ou ambiante &gt; 45°C</span>
                  <YesNo value={extra.atm_chaleur} onChange={(v) => setExtra('atm_chaleur', v)} testid="ec-atm-chaleur" />
                </div>
                <div>
                  Type de protection :
                  <span className="ml-2 inline-flex gap-2 flex-wrap">
                    <Checkbox checked={extra.prot_masque_p3} onChange={(v) => setExtra('prot_masque_p3', v)} label="Masque P3" testid="ec-prot-p3" className="inline-flex" />
                    <Checkbox checked={extra.prot_masque_gaz} onChange={(v) => setExtra('prot_masque_gaz', v)} label="Masque à gaz" testid="ec-prot-mg" className="inline-flex" />
                    <Checkbox checked={extra.prot_cagoule} onChange={(v) => setExtra('prot_cagoule', v)} label="Cagoule ventilée" testid="ec-prot-cag" className="inline-flex" />
                    <Checkbox checked={extra.prot_air_deporte} onChange={(v) => setExtra('prot_air_deporte', v)} label="Air déporté" testid="ec-prot-ad" className="inline-flex" />
                  </span>
                </div>
              </div>
            </div>
            <div>
              <div className="font-bold text-[11px] uppercase tracking-wider mb-2">1.1.2 Créée par les travaux</div>
              <div className="space-y-1.5 pl-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Travaux de soudage ou découpage = Permis de feu requis</span>
                  <YesNo value={extra.trav_soudage} onChange={(v) => setExtra('trav_soudage', v)} testid="ec-trav-soud" />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex-1">Utilisation de peintures, solvants ou produits chimiques</span>
                  <YesNo value={extra.trav_chimique} onChange={(v) => setExtra('trav_chimique', v)} testid="ec-trav-chim" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 1.2 Moyens de prévention */}
        <div>
          <SectionTitle>1.2 Moyens de prévention à mettre en place</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-stone-50/50">
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.1</b> Contrôle d'atmosphère (avant + pendant le travail)</span>
              <YesNo value={extra.ctrl_atmosphere} onChange={(v) => setExtra('ctrl_atmosphere', v)} testid="ec-ctrl-atm" />
            </div>
            <div className="grid grid-cols-4 gap-2 text-[11px] pl-3">
              <div>O₂ : <NumInput value={extra.mes_o2} onChange={(v) => setExtra('mes_o2', v)} testid="ec-o2" suffix="%" width="w-14" /></div>
              <div>CO : <NumInput value={extra.mes_co} onChange={(v) => setExtra('mes_co', v)} testid="ec-co" suffix="ppm" width="w-14" /></div>
              <div>LIE : <NumInput value={extra.mes_lie} onChange={(v) => setExtra('mes_lie', v)} testid="ec-lie" suffix="%" width="w-14" /></div>
              <div>H₂S : <NumInput value={extra.mes_h2s} onChange={(v) => setExtra('mes_h2s', v)} testid="ec-h2s" suffix="ppm" width="w-14" /></div>
            </div>
            <div className="text-[10px] italic text-stone-500 pl-3">Limites : O₂ 19,5-22,5% • CO 50ppm VME / 200ppm VLE • LIE 10% • H₂S 5ppm VME / 10ppm VLE</div>

            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.2</b> Consignation de dispositif d'inertage</span>
              <YesNo value={extra.consignation_inertage} onChange={(v) => setExtra('consignation_inertage', v)} testid="ec-cons-inert" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.3</b> Ventilation (forcée et permanente) — débit min &gt; 30 m³/h/intervenant</span>
              <YesNo value={extra.ventilation} onChange={(v) => setExtra('ventilation', v)} testid="ec-vent" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px] pl-3">
              <div>Volume enceinte : <NumInput value={extra.volume_enceinte} onChange={(v) => setExtra('volume_enceinte', v)} testid="ec-vol" suffix="m³" /></div>
              <div>Débit min ventilation : <NumInput value={extra.debit_ventilation} onChange={(v) => setExtra('debit_ventilation', v)} testid="ec-debit" suffix="m³/h" /></div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.4</b> Contrôle de température</span>
              <YesNo value={extra.ctrl_temperature} onChange={(v) => setExtra('ctrl_temperature', v)} testid="ec-ctrl-temp" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.5</b> Port d'un équipement d'assistance respiratoire (ARI / cagoule…)</span>
              <YesNo value={extra.ari} onChange={(v) => setExtra('ari', v)} testid="ec-ari" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1"><b>1.2.6</b> Certificat de dégazage (ex: cuve carburant)</span>
              <YesNo value={extra.certif_degazage} onChange={(v) => setExtra('certif_degazage', v)} testid="ec-degaz" />
            </div>
          </div>
        </div>

        {/* 2. Enfouissement */}
        <div>
          <SectionTitle accent="bg-orange-700">2. Enfouissement / Ensevelissement</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-orange-50/30">
            <div className="flex items-center justify-between gap-2">
              <span className="flex-1">Risque d'enfouissement présent ?</span>
              <YesNo value={extra.risque_enfouissement} onChange={(v) => setExtra('risque_enfouissement', v)} testid="ec-enfouiss" />
            </div>
            <textarea rows={2} value={extra.mesures_enfouissement || ''} onChange={(e) => setExtra('mesures_enfouissement', e.target.value)}
              data-testid="ec-mes-enf" className="w-full px-2 py-1 text-xs border border-stone-300"
              placeholder="Mesures de prévention prévues (vidange, blindage, surveillance…)" />
          </div>
        </div>

        {/* Plan de secours EC */}
        <div>
          <SectionTitle accent="bg-[#DA291C]">Plan de secours EC</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 space-y-2 text-xs bg-red-50/30">
            <Checkbox checked={extra.surveillant_externe} onChange={(v) => setExtra('surveillant_externe', v)} label="Surveillant externe en permanence à l'extérieur de l'EC (formation EC à jour)" testid="ec-surv-ext" />
            <Checkbox checked={extra.moyen_communication} onChange={(v) => setExtra('moyen_communication', v)} label="Moyen de communication permanent surveillant ↔ intervenant" testid="ec-comm" />
            <Checkbox checked={extra.equip_evac_present} onChange={(v) => setExtra('equip_evac_present', v)} label="Équipement d'évacuation présent (trépied + treuil + harnais)" testid="ec-evac" />
            <div>Référence du plan de secours :
              <input type="text" value={extra.plan_secours_ref || ''} onChange={(e) => setExtra('plan_secours_ref', e.target.value)}
                data-testid="ec-plan-ref" className="ml-2 px-2 py-1 text-xs border border-stone-300 w-1/2" />
            </div>
            <div className="font-mono text-[11px] text-[#DA291C]">N° appel secours : 02 43 66 4442</div>
          </div>
        </div>

        {/* Signataires */}
        <div>
          <SectionTitle>Signataires</SectionTitle>
          <div className="border border-stone-200 border-t-0 p-3 grid sm:grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1 flex items-center gap-2">
                Responsable Lafarge
                {correspondantLafarge && extra.resp_lafarge === correspondantLafarge && (
                  <span className="text-[8px] px-1 py-0.5 bg-blue-100 text-blue-800 normal-case tracking-normal font-normal">auto-import AT</span>
                )}
              </div>
              <input type="text" value={extra.resp_lafarge || ''} onChange={(e) => setExtra('resp_lafarge', e.target.value)}
                data-testid="ec-resp-lafarge" className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom, fonction (auto-importé du Correspondant Lafarge)" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-600 mb-1 flex items-center gap-2">
                Responsable EE
                {correspondantEE && extra.resp_ee === correspondantEE && (
                  <span className="text-[8px] px-1 py-0.5 bg-blue-100 text-blue-800 normal-case tracking-normal font-normal">auto-import AT</span>
                )}
              </div>
              <input type="text" value={extra.resp_ee || ''} onChange={(e) => setExtra('resp_ee', e.target.value)}
                data-testid="ec-resp-ee" className="w-full px-2 py-1.5 border border-stone-300" placeholder="Nom, fonction (auto-importé du Correspondant EE)" />
            </div>
            <div className="sm:col-span-2">
              <FormationPicker
                module="espace_confine"
                site={siteCode}
                value={extra.surveillant_ec || ''}
                onChange={(name) => setExtra('surveillant_ec', name)}
                testid="ec-surveillant"
                placeholder="Surveillant EC — formé Espace Confiné"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
